import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Button, Table, Tooltip } from "antd";
import ReportsGemstoneService from "../../../../services/reports-Gemstone.service";
import { BlobToDownloadFile } from "../../../../helper/commanFunction";

// Self-fetching click-event log, backed by DimondChart_SortingBindData (the
// same endpoint powering the "Recent Gemstone Attribute Searches" table on the
// Gemstone Charts page) rather than GetGemstoneReportForTableChartAll — the
// latter's GemstoneVendorTableChartAll SP has no verified "Clicks" reportName
// branch, so this reuses the endpoint already proven to return an unfiltered,
// dealer-scoped event log.
const ClickEventsTable = ({
  dealerId,
  forDealer,
  dataRangeFrom,
  dataRangeTo,
  selectedGemstoneValue,
  dealerCompany,
  refetchToken,
  title,
}) => {
  const { t } = useTranslation();

  const [dataSource, setDataSource] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [eventsCount, setEventsCount] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sortColumnName, setSortColumnName] = useState("GT.MODIFIED");
  const [sortType, setSortType] = useState("DESC");
  const [searchQuery, setSearchQuery] = useState("");

  const fetchData = (pageNumber, size, sortCol, sortOrder) => {
    if (dealerId == null) return;
    setTableLoading(true);
    ReportsGemstoneService.GemstoneChart_SortingBindData({
      dealerID: dealerId,
      forDealer: forDealer ? forDealer.toString() : "",
      orderBy: "",
      pagging: 0,
      fromDate: dataRangeFrom ? moment(dataRangeFrom).format("YYYY-MM-DD") : "2020-08-17",
      toDate: dataRangeTo ? moment(dataRangeTo).format("YYYY-MM-DD") : "2022-09-15",
      lastAccessDate: dataRangeTo ? moment(dataRangeTo).format("YYYY-MM-DD") : "2022-09-15",
      noOfRows: 0,
      selectedGemstoneValue: selectedGemstoneValue,
      pageNumber: pageNumber || 1,
      pageSize: size || 15,
      sortOrderCol: sortCol || "GT.MODIFIED",
      sortOrderType: sortOrder || "DESC",
    })
      .then((response) => {
        setTableLoading(false);
        const responseData = response.data.responseData;
        const list = responseData?.TableData?.Table || [];
        const total = list.length > 0 ? list[0].TotalCount : 0;
        if (response.data.message === "Success" && responseData && Object.keys(responseData).length !== 0) {
          setDataSource(list);
          setEventsCount(list.length);
          setTotalCount(total);
        } else {
          setDataSource([]);
          setEventsCount(0);
          setTotalCount(0);
        }
      })
      .catch(() => {
        setTableLoading(false);
        setDataSource([]);
        setTotalCount(0);
      });
  };

  useEffect(() => {
    setPageNo(1);
    setSearchQuery("");
    fetchData(1, pageSize, sortColumnName, sortType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerId, forDealer, refetchToken]);

  const handleTableChange = (pagination, _filters, sorter) => {
    const tableBody = document.querySelector(".edr-click-events .ant-table-body");
    if (tableBody) tableBody.scrollTop = 0;

    let type = "DESC";
    let column = "GT.MODIFIED";
    if (sorter.order === "descend") {
      type = "DESC";
      column = sorter.field;
    } else if (sorter.order === "ascend") {
      type = "ASC";
      column = sorter.field;
    }

    setSortType(type);
    setSortColumnName(column);
    setPageNo(pagination.current);
    setPageSize(pagination.pageSize);
    fetchData(pagination.current, pagination.pageSize, column, type);
  };

  const handleExportCSV = () => {
    setCsvLoading(true);
    ReportsGemstoneService.ExportToCSV({
      dealerID: dealerId.toString(),
      hdnFromDate: dataRangeFrom,
      hdnToDate: dataRangeTo,
      hdnPaggingRun: "",
      hdnForDealer: forDealer ? forDealer.toString() : "",
      hdnRetailerFilterId: "",
      hdnOrderBy: "GT.Modified DESC",
      dealerCOMPANY: "",
      checkVendorMembership: "",
      selectedGemstoneValue: selectedGemstoneValue,
    })
      .then((response) => {
        setCsvLoading(false);
        const fileName = `${(dealerCompany || "").replace(/\s+/g, "")}_GemstoneClicksReports_${new Date()
          .toJSON()
          .slice(0, 10)
          .split("-")
          .slice(1)
          .concat(new Date().toJSON().slice(0, 4))
          .join("_")}.csv`;
        BlobToDownloadFile(response.data, fileName);
      })
      .catch(() => setCsvLoading(false));
  };

  const handleExportPDF = () => {
    setPdfLoading(true);
    ReportsGemstoneService.ExportTableDataToPDF({
      dealerID: dealerId,
      forDealer: forDealer ? forDealer.toString() : "",
      fromDate: dataRangeFrom ? moment(dataRangeFrom).format("YYYY-MM-DD") : "2020-08-17",
      toDate: dataRangeTo ? moment(dataRangeTo).format("YYYY-MM-DD") : "2022-09-15",
      lastAccessDate: dataRangeTo ? moment(dataRangeTo).format("YYYY-MM-DD") : "2022-09-15",
      noOfRows: 0,
      orderBy: "",
      pageNumber: 1,
      pageSize: 15,
      pagging: 0,
      selectedGemstoneValue: selectedGemstoneValue,
      sortOrderCol: "GT.MODIFIED",
      sortOrderType: "DESC",
    })
      .then((response) => {
        setPdfLoading(false);
        const fileName = "Chart" + new Date().toJSON().slice(0, 10).split("-").reverse().join("/") + ".pdf";
        BlobToDownloadFile(response.data, fileName, "pdf");
      })
      .catch(() => setPdfLoading(false));
  };

  const visibleRows = useMemo(() => {
    if (!searchQuery.trim()) return dataSource;
    const q = searchQuery.trim().toLowerCase();
    return dataSource.filter((row) =>
      Object.values(row).some((v) => String(v ?? "").toLowerCase().includes(q))
    );
  }, [dataSource, searchQuery]);

  return (
    <div className="edr-panel mt-4">
      <div className="edr-panel-head">
        <div>
          <h3>{title || t("Recent Click Events")}</h3>
          <div className="edr-panel-desc">
            {t("{{count}} / {{total}} events", { count: eventsCount, total: totalCount })}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button className="ghost-btn" onClick={handleExportPDF} loading={pdfLoading}>
            <i className="fa fa-download"></i> {t("PDF")}
          </Button>
          <Button className="ghost-btn" onClick={handleExportCSV} loading={csvLoading}>
            <i className="fa fa-download"></i> {t("CSV")}
          </Button>
        </div>
      </div>

      <div className="edr-tblbar">
        <input
          className="edr-tblsearch"
          placeholder={t("Search this page: IP, shape, color, clarity…")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <span className="edr-badge">{t("{{count}} rows", { count: totalCount })}</span>
      </div>

      <div className="edr-detail-table edr-click-events">
        <Table
          className="pendjewelreq__tbl"
          scroll={{ x: 1200, y: "calc(100vh - 340px)" }}
          loading={tableLoading}
          columns={[
            { title: <Tooltip title={t("IPAddress")}>{t("IPAddress")}</Tooltip>, ellipsis: true, dataIndex: "UsersIPAddress", sorter: () => {} },
            { title: <Tooltip title={t("Stock #")}>{t("Stock #")}</Tooltip>, ellipsis: true, dataIndex: "DealerStockNo", sorter: () => {} },
            { title: <Tooltip title={t("Dealer Company")}>{t("Dealer Company")}</Tooltip>, ellipsis: true, dataIndex: "DealerCompany", sorter: () => {} },
            { title: <Tooltip title={t("Shape")}>{t("Shape")}</Tooltip>, ellipsis: true, dataIndex: "Cut", sorter: () => {} },
            { title: <Tooltip title={t("Size")}>{t("Size")}</Tooltip>, ellipsis: true, dataIndex: "Size", sorter: () => {} },
            { title: <Tooltip title={t("Cut")}>{t("Cut")}</Tooltip>, ellipsis: true, dataIndex: "CutGrade", sorter: () => {} },
            { title: <Tooltip title={t("Color")}>{t("Color")}</Tooltip>, ellipsis: true, dataIndex: "Color", sorter: () => {} },
            { title: <Tooltip title={t("Clarity")}>{t("Clarity")}</Tooltip>, ellipsis: true, dataIndex: "Clarity", sorter: () => {} },
            { title: <Tooltip title={t("Cert")}>{t("Cert")}</Tooltip>, ellipsis: true, dataIndex: "Certificate", sorter: () => {} },
            {
              title: <Tooltip title={t("Price")}>{t("Price")}</Tooltip>,
              ellipsis: true,
              dataIndex: "WholesalePrice",
              sorter: () => {},
              render: (row, item) => {
                const value = item.WholesalePrice ? item.WholesalePrice : "Call For Price";
                return (
                  <Tooltip title={value}>
                    <span>{value}</span>
                  </Tooltip>
                );
              },
            },
            {
              title: <Tooltip title={t("Day & Time")}>{t("Day & Time")}</Tooltip>,
              ellipsis: true,
              dataIndex: "Modified",
              defaultSortOrder: "descend",
              sorter: (a, b) => new Date(a.Modified) - new Date(b.Modified),
              render: (value) => {
                if (!value) return null;
                const formattedDate = moment(value).format("MM-DD-YYYY, hh:mm:ss A");
                return (
                  <Tooltip title={formattedDate}>
                    <span>{formattedDate}</span>
                  </Tooltip>
                );
              },
            },
          ]}
          dataSource={visibleRows}
          pagination={{
            size: "small",
            total: totalCount,
            pageNo: pageNo,
            pageSize: pageSize,
            pageSizeOptions: [10, 15, 20, 50, 100],
            responsive: true,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
        />
      </div>
    </div>
  );
};

export default ClickEventsTable;
